import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ArrowRight, Video, Volume2, VolumeX } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

/** "Seja sócio investidor" banner — lives on the Profile page. */
export function InvestorBanner() {
  return (
    <Card className="p-5 relative overflow-hidden border-primary/50 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent" data-testid="investor-banner">
      <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-primary/20 blur-3xl" aria-hidden="true" />
      <div className="relative flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/40">
          <TrendingUp className="w-7 h-7 text-primary-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary">🚀 Convite exclusivo</div>
          <h3 className="text-lg font-bold tracking-tight leading-tight">Seja sócio investidor do FUTURO PDV</h3>
          <p className="text-xs text-muted-foreground">Rendimentos a partir de 8% a.a. + participação no crescimento da plataforma.</p>
        </div>
        <Link to="/investors" data-testid="investor-banner-cta">
          <Button size="sm" className="rounded-full gap-1 whitespace-nowrap">
            Investir <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    </Card>
  );
}

const readBool = (key, fallback) => {
  const v = localStorage.getItem(key);
  return v === null ? fallback : v === "true";
};

/** Feed video preferences (autoplay + sound). Persists to the same localStorage keys the Feed reads. */
export function VideoAutoplayCard() {
  const [autoplayEnabled, setAutoplayEnabled] = useState(() => readBool("feed_video_autoplay", true));
  const [videoMuted, setVideoMuted] = useState(() => readBool("feed_video_muted", true));
  useEffect(() => { localStorage.setItem("feed_video_autoplay", String(autoplayEnabled)); }, [autoplayEnabled]);
  useEffect(() => { localStorage.setItem("feed_video_muted", String(videoMuted)); }, [videoMuted]);

  return (
    <Card className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="video-autoplay-bar">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${autoplayEnabled ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
          <Video className="w-4 h-4" />
        </div>
        <div>
          <div className="text-sm font-semibold">Autoplay de vídeos</div>
          <div className="text-xs text-muted-foreground">
            {autoplayEnabled ? "Vídeos tocam automaticamente ao rolar o feed" : "Vídeos ficam pausados com controles"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setVideoMuted((m) => !m)}
          data-testid="video-sound-toggle"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            videoMuted ? "border-border text-muted-foreground hover:bg-muted" : "border-primary bg-primary/10 text-primary"
          }`}
          aria-label={videoMuted ? "Ativar som dos vídeos" : "Silenciar vídeos"}
        >
          {videoMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          {videoMuted ? "Som off" : "Som on"}
        </button>
        <Switch checked={autoplayEnabled} onCheckedChange={setAutoplayEnabled} data-testid="video-autoplay-toggle" />
      </div>
    </Card>
  );
}
